/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 * This module defines all the enum types that we use in datavoke
 */

// #region Entities

/**
 * Enumeration for Entity States
 * @readonly
 * @enum {number}
 */
export var EntityStates = {
    NewObject: 0,
    Unmodified: 1,
    Modified: 2,
    Deleted: 3,
    CopyObject: 4
};

/**
 * Enumeration for Entity Types
 * @readonly
 * @enum {string}
 */
export var EntityTypes = {
    RecordType: "RecordType",
    Category: "Category",
    Property: "Property",
    ValueStatus: "ValueStatus",
    View: "View",
    Security: "Security",
    SecurityGroup: "SecurityGroup",
    SecurityGroupLookups: "SecurityGroupLookups",
    Style: "Style",
    Tag: "Tag",
    TagLink: "TagLink",
    Package: "Package",
    PendingChange: "PendingChange",

    //#region Extensions
    Expression: "Expression",
    Action: "Action",
    Widget: "Widget",
    Adapter: "Adapter",
    Endpoint: "Endpoint",
    //#endregion
};

// #endregion

/**
 * Message types enumeration...eg
 * @readonly
 * @enum {number}
 */
export var MessageType = {
    Info: 0,
    Error: 1,
    Confirm: 2,
    Toast: 3,
    Console: 4
};

/**
 * Message types enumeration...eg
 * @readonly
 * @enum {number}
 */
export var MessageTypes = {
    Information: 0,
    Warning: 1,
    Question: 2,
    Toast: 3,
    Console: 4
};

/**
 * Enumeration for horizontal text alignment
 * @readonly
 * @enum {number}
 */
export var HorizontalTextAlignment = {
    Left:1,
    Center: 16,
    Right:2
};

/**
 * Enumeration for icon position in buttons
 * @readonly
 * @enum {number}
 */
export var ButtonIconPosition = {
    Top: 0,
    Left: 1,
    Right: 2,
    Bottom: 3,
    None: 4
};

// #region Views

/**
 * Enumeration for types of View entities
 * @readonly
 * @enum {number}
 */
export var ViewType = {
    HierarchyView: 0,
    GridView: 1,
    ReportView: 2,
    SearchView: 3,
    //AnalysisView : 4,
    DocBuilderView: 5,
    LinkerView: 6,
    DashboardView: 7,
    FormCompletionView: 8,
    SubGridView: 9,
    LayoutView: 10,
    DiagramView: 11,
    PlaceHolderView: 12,
    RelatedReportView: 13
};

/**
 * Enumeration for mode of entering new records
 * @readonly
 * @enum {number}
 */
export var NewRecordModes = {
    Default: 0,
    FormViewPopOut: 1
};

/**
 * Enumeration for aggregate function to use for summaries
 * @readonly
 * @enum {number}
 */
export var DataVokeSummaryItemType = {
    Sum: 0,
    Min: 1,
    Max: 2,
    Count: 3,
    Average: 4,
    Custom: 5,
    None: 6
};

/**
 * Enumeration for which side of the grid fixed columns should be shown on
 * @readonly
 * @enum {number}
 */
export var ColFixed = {
    None: 0,
    Left: 1,
    Right: 2
};

/**
 * Enumeration for type of dimensions used for view display
 * @readonly
 * @enum {number}
 */
export var ViewDimensionType = {
    Pixels: 0,
    Percent: 1
};

// #endregion

// #region Macros

/**
 * Enumeration for the type number of core actions
 * @readonly
 * @enum {number}
 * @deprecated
 */
export var ActionTypes = {
    New: 0,
    Save: 1,
    Close: 2,
    Delete: 3,
    OpenView: 4,
    SetStatus: 5,
    CaptureImage: 6,
    First: 7,
    Previous: 8,
    Last: 9,
    Next: 10,
    Link: 11,
    UnLink: 12,
    Join: 13,
    Undo: 14,
    ReAuth: 15,
    Refresh: 16,
    Import: 17,
    Export: 18,
    Special: 19,
    ShowMessage: 20,
    PromptUser: 21,
    PromptForValue: 22,
    ShowForm: 23,
    PopoutForm: 24,
    RefreshRemoteView: 25,
    GoHome: 26,
    RemoveRecord: 27,
    SetValue: 28,
    CloseForm: 29,
    PromptForAttachment: 30,
    ViewTemplate: 31,
    UnJoin: 32,
    PopinForm: 33,
    RemoteViewMacro: 34,
    IfMacro: 35,
    ForEachAction: 36,
    ReportMacro: 37,
    Insert: 38,
    MoveRecordUp: 39,
    MoveRecordDown: 40,
    MoveRecordTop: 41,
    MoveRecordBottom: 42,
    SpellCheck: 44,
    GroupMacro: 45,
    LinkSelected: 46,
    StoreVar: 47,
    CopyDataStructure: 48,
    ImportMatrix: 49,
    SetBusySpinner: 50,
    SetSelected: 51,
    SetSecurity: 52,
    Navigation: 53,
    RecordMove: 54,
    UploadFiles: 55,
    ForceFocus: 56,
    OfflineRecordSync: 57,
    Hyperlink: 58,
    AlterView: 59,
    Wait: 60,
    DownloadAttachment: 61,
    UserCreateAccount: 62,
    UserDisableAccount: 63,
    UserModifyGroups: 64,
    UserResetAccount: 65,
    ReportToPDF: 66,
    AddDocumentToCombine: 67,
    GetCombinedDocument: 68,
    RefreshStyle: 69,
    RecalcAggregate: 70,
    RunJavaScript: 71,
    ForEachBreak: 72,
    SendEmail: 73,
    OpenFieldHistory: 74,
    SwitchUser: 75,
    CalloutForm: 76,
    LoadEmbeddedForm: 77,
    SignOut: 78,
    StartInterval: 79,
    ClearInterval: 80,
    CancelEvent: 81,
    FirstPage: 82,
    NextPage: 83,
    PrevPage: 84,
    LastPage: 85,
    GotoPage: 86,
    SetFormPageSize: 87,
    ShowUserTabBar: 88,
    HideUserTabBar: 89,
    HandleConnectionEvents: 90,
    ClearAttachment: 91
};

/**
 * Enumeration for the UID strings of core actions
 * @readonly
 * @enum {string}
 */
export var ActionUIDs = {
    AddDocumentToCombine: "c362f219-b900-4fd4-8979-8721c62805d8",
    AlterView: "f0b250c0-b2fd-45aa-a0f1-dc1fc6f01c74",
    CancelEvent: "7d204ad8-5c2d-482f-8514-9d7572d98c00",
    CaptureImage: "108e47a5-66d0-4aff-ac47-51a0f4861ab2",
    CloseView: "ec413fdd-fd4d-425d-9466-586479c0100f",
    DeleteRecord: "e9aaf552-c5e9-447a-b627-2fadd4f9cb2b",
    DownloadAttachment: "837d16ea-6711-4c00-a6ba-c6226c9dc9c7",
    ExportData: "11c8f37f-d57a-4058-8588-8329bf1157e5",
    ForceFocus: "8312901a-d714-496d-be63-4ebfbb4d023a",
    ForEach: "371242ef-d6b0-4c7b-8428-54def0683536",
    GetCombinedDocument: "bb1e8c01-b920-4eee-9000-93fe422c1071",
    GroupAction: "77a99dc8-ea23-4d44-9636-3c78b4b6593f",
    Hyperlink: "ca0979fa-4295-4e9c-9965-c5bbb6138c9d",
    If: "7eef8a0c-4e57-4e59-a7d5-9a37e36908f2",
    ImportData: "6e36144f-5d82-4bc4-95ab-b904b9ca9361",
    Join: "bb8c35df-8221-400d-9edf-7c3294327bac",
    LinkSelected: "114f31b2-f627-4c76-910f-8ea75ff61c55",
    Navigation: "013c0d21-b340-4989-a2b6-2b409f3d9cbc",
    NewRecord: "98cdea3a-8f48-4dce-83c1-16a21a4d2daa",
    OpenView: "b9e22d57-4e8d-415c-9fdb-5c043f9baed1",
    OverlayPDF: "3fd6fb6c-9f3a-42da-b1e2-4588dd715fe8",
    PromptForValue: "0998b507-c20f-4623-9fb9-4c0ef6d9fba3",
    Reauth: "ae0f89d7-3471-41b6-87ac-2f4f4b5e76f4",
    RecalcAggregate: "89d8b7e3-7c31-42a7-bff1-53a508bf532b",
    MoveRecord: "833fb4d0-7d45-4de1-8690-b048c7a5e4b4",
    RefreshStyle: "4343b18a-8d65-4d6a-9763-db67b55aec1f",
    RefreshData: "efa81ec6-573d-40b9-9b1b-2c60982f3140",
    CallActionChain: "0d5a53a4-8a6c-4fc8-a260-6f8c830be7e2",
    RemoveRecord: "3a66b08c-a083-4ed7-a2ec-897bc391c1f7",
    ReportToPDF: "b76ec9cf-4aef-45c5-8387-eb0203d79863",
    RunJavaScript: "ff0c5357-abb3-461d-a10b-910f8cea94d5",
    Save: "1b232a25-de38-4cf5-80a6-391ef06162a3",
    SendEmail: "cf7cfcd7-6080-4bcf-bc67-c3ef387b4ddf",
    SetBusySpinner: "e0fd3e26-f85e-4837-ba59-3dda283fc47c",
    SetSelected: "353b77c3-86b2-4290-a9a3-cbe62029a7c7",
    SetStatus: "58723443-dab9-4898-aef3-f749975ba20e",
    SetValue: "c694b119-4bfd-43ef-9903-8f86e315b68f",
    ShowMessage: "17d16f85-196a-45d8-b35f-c8f9829bc801",
    StoreVar: "33b093f6-73f0-4e4e-ab8a-1577228413ec",
    Undo: "abd7fe15-d9a9-44a1-aaa4-f53143b2b35b",
    Unjoin: "4dde7507-96de-4823-85af-b3f8c54f4a65",
    Unlink: "932018b2-e068-46ad-882b-4204a44402e3",
    UploadFiles: "16ea7297-3862-425c-8b83-cbe92a8bf0a1",
    UserCreateAccount: "a10256e2-0082-47c8-a59a-d5af46e16121",
    UserDisableAccount: "a1cecf4b-91db-49d0-9855-5d9b5e60102d",
    UserModifyGroups: "e971586b-e13f-4d86-aff0-c3cdc2b0b828",
    UserResetAccount: "c722b874-6bf2-4849-8bf9-d71d6378e277",
    ViewTemplate: "890f5f3f-046f-4d71-8d79-a6807b0fe1f7",
    Wait: "1e61c6a7-72ed-457d-b0b7-6bf0be7ce137",
    IfExists: "bf4e6d94-575a-4d9a-a9b5-92081428c318",
    OpenFieldHistory: "5f2a0828-a85f-11e7-abc4-cec278b6b50a",
    SwitchUser: "ed96b980-21db-452d-b3e8-bc144eafa547",
    CalloutForm: "6dd2f292-a062-40e7-a549-3ccaf4d6f058",
    LoadEmbeddedForm: "ad490cf5-75ae-4984-8879-478f011a58f0",
    SignOut: "592069f4-69a7-11e8-adc0-fa7ae01bbebc",
    StartInterval: "4c1b6ef7-a4ce-4a12-97c4-b611ef242d88",
    ClearInterval: "e68c66a8-2c75-44c3-9950-c44be7a62c38",
    FirstPage: "442804ff-f9ca-4fe1-aebf-e721d5f62586",
    NextPage: "6c4fd8f2-73f8-40b5-9008-af62fcd06280",
    PrevPage: "12a8dd24-1715-48a0-88c6-fc50a35f3b98",
    LastPage: "29032f43-5be1-4d22-b86e-001b3c48b2c0",
    GotoPage: "090b9faa-92c8-4dd9-bd6b-eec659d231d7",
    SetFormPageSize: "b07aa0fa-b2e2-11e8-8ff5-529269fb1459",
    ShowUserTabBar: "ff358564-e850-4825-a815-54b4af1f187a",
    HideUserTabBar: "1791a1e3-9d4e-4646-b0d2-a3007b72c6cc",
    HandleConnectionEvents: "38e1ff1e-af45-4184-ba53-a3f46485eeef",
    ClearAttachment: "9e6f02ef-648d-4bcc-875e-f1a15c8f1a0f",
    BlockComment: 'aa30d6f4-910f-493f-ba2e-fd94b046444e',
};

/**
 * Enumeration for how to trigger task chains in an MVC
 * @readonly
 * @enum {number}
 * @deprecated
 */
export var ViewMacroTriggers = {
    Open: 0,
    OnDemand: 1,
    Close: 2
};

/**
 * Enumeration for the navigation step to take
 * @readonly
 * @enum {number}
 */
export var NavigationType = {
    Next: 0,
    Previous: 1,
    First: 2,
    Last: 3
};

/**
 * Enumeration for the different focus methods of the force focus action
 * @enum {number}
 */
export var FocusControlMethods = {
    ID: 1,
    Column: undefined
};

/**
 * Enumeration for where to move a record
 * @readonly
 * @enum {number}
 */
export var RecordMoveType = {
    Top: 0,
    Bottom: 1,
    Up: 2,
    Down: 3
};

/**
 * Enumeration to indicate where to insert a record relative to the current selected record of an MVC
 * @readonly
 * @enum {number}
 */
export var InsertAt = {
    Default: 0,
    Above: 1,
    Below: 2
};

/**
 * Enumeration for view aspects (Alter View action)
 * @readonly
 * @enum {number}
 */
export var ViewAspect = {
    View: 0,
    Column: 1,
    Macro: 2
};

/**
 * Enumeration for aspect settings (Alter View action)
 * @readonly
 * @enum {Object}
 */
export var AspectSettings = {
    /**
     * Enumeration for form aspect settings
     * @readonly
     * @enum {number}
     */
    Form: {
        DisplayName: 0,
        GroupsExpaned: 1,
        Visible: 2,
        Maximized: 3
    },

    /**
     * Enumeration for column aspect settings
     * @readonly
     * @enum {number}
     */
    Column: {
        Alias: 4,
        Operator: 5,
        Value: 6,
        Grouped: 7,
        Hidden: 8,
        ReadOnly: 9,
        Sort: 10
    },

    /**
     * Enumeration for task aspect settings
     * @readonly
     * @enum {number}
     */
    Task: {
        Enabled: 11,
        Collapsed: 12,
        Visible: 13,
        Label: 14
    }
};

/**
 * Enumeration for window open states for forms when opened
 * @readonly
 * @enum {number}
 */
export const OPEN_MODES = {
    NORMAL: 0,
    HIDDEN: 1,
    MODAL: 2,
    MAXIMIZED: 3
};

// #endregion

// #region Properties

/**
 * Enumeration for data types of property entities
 * @readonly
 * @enum {number}
 */
export var PropertyDataType = {
    Bit: 0,
    Decimal: 1,
    DateTime: 2,
    Integer: 3,
    Text: 4,
    Binary: 5,
    RTF: 6,
    InlineRTF: 7,
    StaticDateTime: 8,
    PrimaryKey: 9,
    List: 10
};

/**
 * Enumeration of aggregation functions to use for summaries
 * @readonly
 * @enum {number}
 */
export var AggregateTypes = {
    Count: 0,
    Sum: 1,
    Average: 2,
    Min: 3,
    Max: 4
    // FilledCount: 5,      //Currently not used...EK
    // FilledPercent: 6
};

/**
 * Enumeration for transaction action types
 * @readonly
 * @enum {number}
 */
export var TransActionTypes = {
    ValueChange: 0,
    AddItem: 1,
    DeleteItem: 2,
    Import: 3,
    Link: 4,
    Unlink: 5,
    ToggleWidget: 6
};

// #endregion

// #region Security

/**
 * Enumeration for security levels
 * @readonly
 * @enum {number}
 */
export var SecurityLevels = {
    None: 0,
    Read: 1,
    Write: 2,
    Delete: 3,
    PromoteFrom: 2,
    PromoteTo: 3
};

// #endregion

/**
 * Enumeration of connection handler types
 * @readonly
 * @enum {number}
 */
export const ConnectionHandlerTypes = {
    restored: 0,
    lost: 1,
    versionChanged: 2
};

/**
 * Enumeration for grid record events
 * @readonly
 * @enum {string}
 */
export const gridRecordEvents = {
    StatusChanged: "StatusChanged"
};

/**
 * Enumerations for layout alignments
 * @type {Object}
 */
export const LayoutDefAlignments = {
    /**
     * Enumeration for vertical alignments
     * @readonly
     * @enum {number}
     */
    VerticalAlignments: {
        TOP: 0,
        CENTER: 1,
        BOTTOM: 2,
        STRETCH: 3
    },

    /**
     * Enumeration for horizontal alignments
     * @readonly
     * @enum {number}
     */
    HorizontalAlignments: {
        LEFT: 0,
        CENTER: 1,
        RIGHT: 2,
        STRETCH: 3
    },

    /**
     * Enumeration for orientation of layouts
     * @readonly
     * @enum {number}
     */
    Orientations: {
        VERTICAL: 0,
        HORIZONTAL: 1
    }
};

/**
 * Enumeration for sources for images
 * @readonly
 * @enum {number}
 */
export var ImageSourceTypes = {
    Url: 0,
    Property: 1,
    Expression: 2,
    Library: 3
};

/**
 * Enumeration for scaling of images
 * @readonly
 * @enum {number}
 */
export var ImageScalingTypes = {
    Fit: undefined, // Default
    Clip: 1,
    Stretch: 2,
    Actual: 3
};

/**
 * Enumeration for interpreting source text for a label definition
 * @readonly
 * @enum {number}
 */
export var LabelTypes = {
    Text: 0,
    Expression: 1
};

/**
 * Enumeration for interpreting source text for a button definition
 * @readonly
 * @enum {number}
 */
export var ButtonTextTypes = {
    Text: undefined,
    Expression: 1
};

/**
 * Enumeration for interpreting source text for a button icon image
 * @readonly
 * @enum {number}
 */
export var ButtonIconSrcTypes = {
    Library: undefined,
    URL: 1,
    Expression: 2
};

/**
 * Enumeration for data sorting
 * @readonly
 * @enum {number}
 */
export var sorting = {
    asc: -1,
    desc: 1,
    none: 0
};

/**
 * Enumeration for modes of report rendering
 * @readonly
 * @enum {number}
 */
export var ReportModes = {
    NewTab: 0,
    Download: 1,
    Attach: 2
};

/**
 * Enumeration for type of view layout definition object
 * @readonly
 * @enum {number}
 */
export const LayoutViewType = {
    Generic: 0,
    GroupPanel: 1,
    Tabs: 2,
};

/**
 * Enumeration for event types valid for data binding triggers
 * @readonly
 * @enum {number}
 */
export const DataBindingEventTypes = {
    FocusLost: 0
};

/**
 * Enumeration for location of icons in a rendered list
 * @readonly
 * @enum {number}
 */
export const ListIconLocationTypes = {
    Default: 0,
    RightAligned: 1
};

/**
 * Enumeration for types of designer notifications
 * @readonly
 * @enum {number}
 */
export const NotificationTypes = {
    Information: 0,
    Warning: 1,
    Error: 2
};

/**
 * Enumeration for types of layout path bar items
 * @readonly
 * @enum {number}
 */
export const LayoutPathBarTypes = {
    TopItem: 0,
    ParentItem: 1,
    SelectedItem: 2,
    ChildItem: 3,
    PreviewItem: 4
};

/**
 * Enumeration for SearchBar position for CardList widget.
 * NOTE: These values are set to the zebra.layout enum for TOP/BOTTOM
 * @readonly
 * @enum {number}
 */
export const SearchBarPositions = {
    Top: 4,
    Bottom: 8
};

/**
 * Enumeration for Hyperlink access types
 * @readonly
 * @enum {number}
 */
export const AccessTypes = {
    Private: 1,
    Public: 2
};

/**
 * Enumeration for types of allowed scrolling in form controls
 * @readonly
 * @enum {number}
 */
export const ScrollTypes = {
    None: 0,
    Auto: 1,
    Scroll: 2
};

// Date DOM element types
export const DateDOMTypes = {
    Date: undefined,
    DateTime: 1,
    Time: 2,
    Month: 3
};

/**
 * Enumeration for socket connection states
 * @readonly
 * @enum {number}
 */
export const SocketStates = {
    CLOSED: 0,
    CONNECTING: 1,
    OPEN: 2,
    AUTHENTICATED: 3
};

/**
 * Extend the application property enumeration for state panels...
 * @readonly
 * @enum {number}
 */
export const PanelStates = {
    OVER: 0,
    PRESSED_OVER: 1,
    OUT: 2,
    PRESSED_OUT: 3,
    DISABLED: 4,
    SELECTED: 5
};

/**
 * These are the string IDs that directly relate to the PanelStates.
 * @readonly
 * @type {Array<string>}
 */
export const PanelStateIDs = [
    "over",
    "pressed.over",
    "out",
    "pressed.out",
    "disabled",
    "selected"
];

/**
 * Enumeration for chart types
 * @readonly
 * @enum {string}
 */
export const ChartTypes = {
    "Pie": "Chart:Pie",
    "Donut": "Chart:Doughnut",
    "Line": "Chart:Line",
    "Bar": "Chart:Bar"
};
